import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Spinner,
  useColorModeValue,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  FiUser,
  FiBriefcase,
  FiFileText,
  FiTrendingUp,
  FiUsers,
  FiFolder,
} from "react-icons/fi";
import PersonalInfoTab from "./PersonalInfoTab";
import JobHistoryTab from "./JobHistoryTab";
import DocumentsTab from "./DocumentsTab";
import { digitalFilesService } from "../digitalFilesService";

const DigitalFiles = () => {
  // Dark mode colors
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Responsive layout
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [stats, setStats] = useState({
    totalEmployees: 0,
    personalInfoRecords: 0,
    jobHistoryRecords: 0,
    totalDocuments: 0,
    verifiedDocuments: 0,
    pendingVerification: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [employees, setEmployees] = useState([]);

  const toast = useToast();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel for statistics
      const [employeesRes, personalInfo, jobHistory, documents] =
        await Promise.all([
          digitalFilesService.getAllEmployees().catch(() => ({ data: [] })),
          digitalFilesService.personalInfo
            .getAllPersonalInfo()
            .catch(() => ({ data: [] })),
          digitalFilesService.jobHistory
            .getAllJobHistory()
            .catch(() => ({ data: [] })),
          digitalFilesService.documents
            .getAllDocuments()
            .catch(() => ({ data: [] })),
        ]);

      const personalInfoData = Array.isArray(personalInfo?.data)
        ? personalInfo.data
        : [];
      const jobHistoryData = Array.isArray(jobHistory?.data)
        ? jobHistory.data
        : [];
      const documentsData = Array.isArray(documents?.data)
        ? documents.data
        : [];
      const employeesData = Array.isArray(employeesRes?.data)
        ? employeesRes.data
        : [];

      // Set employees data
      setEmployees(employeesData);

      // Calculate statistics
      const verifiedDocs = documentsData.filter(
        (doc) => doc.verificationStatus === "Verified"
      ).length;
      const pendingDocs = documentsData.filter(
        (doc) => doc.verificationStatus === "Pending"
      ).length;

      setStats({
        totalEmployees: employeesData.length,
        personalInfoRecords: personalInfoData.length,
        jobHistoryRecords: jobHistoryData.length,
        totalDocuments: documentsData.length,
        verifiedDocuments: verifiedDocs,
        pendingVerification: pendingDocs,
      });
    } catch (error) {
      console.error("Error fetching statistics:", error);
      toast({
        title: "Error loading statistics",
        description: "Could not load dashboard statistics",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDataUpdate = () => {
    fetchStats(); // Refresh statistics when data is updated
  };

  if (loading) {
    return (
      <Box textAlign="center" py={12}>
        <Spinner size="xl" color="blue.500" mb={4} />
        <Text>Loading digital files dashboard...</Text>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }} maxW="100%" mx="auto">
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        {/* Header */}
        <Box>
          <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
            Digital Employee Files
          </Text>
          <Text color={mutedColor} fontSize={{ base: "sm", md: "md" }}>
            Comprehensive employee information management system
          </Text>
        </Box>

        {/* Statistics Cards - Horizontal scroll on mobile */}
        <Box
          overflowX="auto"
          css={{
            "&::-webkit-scrollbar": {
              height: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "gray.300",
              borderRadius: "2px",
            },
          }}
        >
          <HStack spacing={3} minW={{ base: "max-content", md: "100%" }} pb={2}>
            <Card
              bg={cardBg}
              borderColor={borderColor}
              minW={{ base: "140px", md: "auto" }}
              flex={{ base: "0 0 auto", md: "1" }}
            >
              <CardBody py={3} px={4}>
                <Stat>
                  <StatLabel fontSize="xs">
                    <HStack spacing={1}>
                      <FiUsers size={14} />
                      <Text>Employees</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber fontSize="2xl">{stats.totalEmployees}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card
              bg={cardBg}
              borderColor={borderColor}
              minW={{ base: "140px", md: "auto" }}
              flex={{ base: "0 0 auto", flex: "1" }}
            >
              <CardBody py={3} px={4}>
                <Stat>
                  <StatLabel fontSize="xs">
                    <HStack spacing={1}>
                      <FiUser size={14} />
                      <Text>Personal Info</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber fontSize="2xl">
                    {stats.personalInfoRecords}
                  </StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card
              bg={cardBg}
              borderColor={borderColor}
              minW={{ base: "140px", md: "auto" }}
              flex={{ base: "0 0 auto", md: "1" }}
            >
              <CardBody py={3} px={4}>
                <Stat>
                  <StatLabel fontSize="xs">
                    <HStack spacing={1}>
                      <FiBriefcase size={14} />
                      <Text>Job History</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber fontSize="2xl">
                    {stats.jobHistoryRecords}
                  </StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card
              bg={cardBg}
              borderColor={borderColor}
              minW={{ base: "140px", md: "auto" }}
              flex={{ base: "0 0 auto", md: "1" }}
            >
              <CardBody py={3} px={4}>
                <Stat>
                  <StatLabel fontSize="xs">
                    <HStack spacing={1}>
                      <FiFolder size={14} />
                      <Text>Documents</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber fontSize="2xl">{stats.totalDocuments}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card
              bg={cardBg}
              borderColor={borderColor}
              minW={{ base: "140px", md: "auto" }}
              flex={{ base: "0 0 auto", md: "1" }}
            >
              <CardBody py={3} px={4}>
                <Stat>
                  <StatLabel fontSize="xs">
                    <HStack spacing={1}>
                      <FiTrendingUp size={14} />
                      <Text>Verified</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber fontSize="2xl" color="green.500">
                    {stats.verifiedDocuments}
                  </StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card
              bg={cardBg}
              borderColor={borderColor}
              minW={{ base: "140px", md: "auto" }}
              flex={{ base: "0 0 auto", md: "1" }}
            >
              <CardBody py={3} px={4}>
                <Stat>
                  <StatLabel fontSize="xs">
                    <HStack spacing={1}>
                      <FiFileText size={14} />
                      <Text>Pending</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber fontSize="2xl" color="orange.500">
                    {stats.pendingVerification}
                  </StatNumber>
                </Stat>
              </CardBody>
            </Card>
          </HStack>
        </Box>

        {/* Main Content Tabs */}
        <Card bg={cardBg} borderColor={borderColor}>
          <CardBody p={{ base: 0, md: 4 }}>
            <Tabs
              variant="enclosed"
              colorScheme="blue"
              index={activeTab}
              onChange={setActiveTab}
            >
              <TabList
                overflowX="auto"
                borderBottom="1px"
                borderColor={borderColor}
                css={{
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  scrollbarWidth: "none",
                }}
              >
                <Tab minW={{ base: "auto", md: "unset" }}>
                  <HStack spacing={2}>
                    <FiUser size={16} />
                    <Text display={{ base: "none", md: "block" }}>
                      Personal Information
                    </Text>
                    <Text display={{ base: "block", md: "none" }}>
                      Personal
                    </Text>
                  </HStack>
                </Tab>
                <Tab minW={{ base: "auto", md: "unset" }}>
                  <HStack spacing={2}>
                    <FiBriefcase size={16} />
                    <Text display={{ base: "none", md: "block" }}>
                      Job History
                    </Text>
                    <Text display={{ base: "block", md: "none" }}>Jobs</Text>
                  </HStack>
                </Tab>
                <Tab minW={{ base: "auto", md: "unset" }}>
                  <HStack spacing={2}>
                    <FiFileText size={16} />
                    <Text>Documents</Text>
                  </HStack>
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel p={{ base: 4, md: 6 }}>
                  <PersonalInfoTab
                    employees={employees}
                    onDataUpdate={handleDataUpdate}
                  />
                </TabPanel>
                <TabPanel p={{ base: 4, md: 6 }}>
                  <JobHistoryTab
                    employees={employees}
                    onDataUpdate={handleDataUpdate}
                  />
                </TabPanel>
                <TabPanel p={{ base: 4, md: 6 }}>
                  <DocumentsTab
                    employees={employees}
                    onDataUpdate={handleDataUpdate}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default DigitalFiles;
